<template>
  <div class="custom-border-outline mt-3">
    <div class="d-flex justify-content-between flex-wrap align-items-center">
      <h5 class="text-evollu-gray-light is-bold mb-0 pl-3 py-3">
        {{$t('iframe.titles.project_details')}}
      </h5>

      <i
        v-if="collapsable"
        class="clickable p-3"
        :class="{'fa fa-chevron-up': !isCollapsed, 'fa fa-chevron-down': isCollapsed}"
        @click="isCollapsed = !isCollapsed"
      >
      </i>
    </div>
    <div v-if="!isCollapsed">
      <form class="evollu-form">
        <div class="row col-12" style="margin-bottom: 0 !important;">
          <!-- input name -->
          <div class="row col-md-6">
            <div class="col-12">
              <va-input
                id="name"
                v-model="value.name"
                :label="$t('iframe.table_headings.project_name')"
                :placeholder="$t('iframe.table_headings.project_name')"
                @input="formErrors.clear('name')"
              />

              <small v-show="formErrors.has('name')" class="help text-danger">
                {{ $t(formErrors.get('name')) }}
              </small>
            </div>
          </div>
          <!-- --- --->

          <!-- client name -->
          <div class="row col-md-6">
            <div class="col-12">
              <label class="form-label">{{ $t('iframe.table_headings.client') }}</label>
              <multiselect
                :options="clientOptions"
                id="client"
                v-model="client"
                label="name"
                track-by="id"
                :disabled="clientOptions.length === 1"
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="formErrors.clear('client_id')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>

              <small v-show="formErrors.has('client_id')" class="help text-danger">
                {{ $t(formErrors.get('client_id')) }}
              </small>
            </div>
          </div>
          <!-- --- --->
        </div>

        <div class="row col-12" style="margin-bottom: 0 !important;">
          <!-- url/hash -->
          <div class="row col-md-6">
            <label class="pl-3 form-label mb-2">{{ $t('iframe.table_headings.url') }}</label>
            <div class="col-12 d-inline-flex">
              <div class="col-7 pl-0 pr-0">
                <label style="font-size: 14px; padding-top: 10px">{{`https://iframe.${envType}.evollu.com?hash=`}}</label>
              </div>
              <div class="col-5 pl-1 pr-0">
                <va-input
                  id="url"
                  v-model="value.hash"
                  :placeholder="$t('iframe.table_headings.url')"
                  @input="formErrors.clear('hash')"
                />
              </div>
              <small v-show="formErrors.has('hash')" class="help text-danger">
                {{ $t(formErrors.get('hash')) }}
              </small>
            </div>
          </div>
          <!-- --- --->

          <!-- lang -->
          <div class="row col-md-6">
            <div class="col-12">
              <label class="form-label">{{ $t('iframe.table_headings.lang') }}</label>
              <multiselect
                :options="langOptions"
                id="lang"
                v-model="value.lang"
                :custom-label="opt => opt.language_key ? `${opt.emoji} ${$t(`language.${opt.language_key}`)}` : 'N/A'"
                track-by="lang"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="formErrors.clear('lang')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>

              <small v-show="formErrors.has('lang')" class="help text-danger">
                {{ $t(formErrors.get('lang')) }}
              </small>
            </div>
          </div>
          <!-- --- --->
        </div>

        <div class="row col-12" style="margin-bottom: 0 !important;">
          <!-- country -->
          <div class="row col-md-6 ">
            <div class="col-12">
              <label class="form-label">{{ $t('iframe.table_headings.country') }}</label>
              <multiselect
                :options="countries"
                id="country"
                v-model="value.country"
                :custom-label="opt => opt.name ? opt.name : 'N/A'"
                track-by="code"
                select-label=""
                selected-label=""
                deselect-label=""
                placeholder="---"
                open-direction="bottom"
                :allow-empty="false"
                :multiple="false"
                :searchable="true"
                @input="formErrors.clear('country')"
              >
                <span slot="noResult">{{ $t('patients.noResults') }}</span>
              </multiselect>

              <small v-show="formErrors.has('country')" class="help text-danger">
                {{ $t(formErrors.get('country')) }}
              </small>
            </div>
          </div>
          <!-- --- --->
        </div>

        <div class="row col-12">
          <!-- Adjust Volume Text -->
          <div class="row col-md-6">
            <div class="col-12">
              <va-input
                id="adjustVolumeText"
                type="textarea"
                :min-rows="2"
                v-model="value.adjust_volume_text"
                :label="$t('iframe.titles.adjust_volume_text')"
                :placeholder="$t('iframe.titles.adjust_volume_text')"
                @input="formErrors.clear('adjust_volume_text')"
              />

              <small v-show="formErrors.has('adjust_volume_text')" class="help text-danger">
                {{ $t(formErrors.get('adjust_volume_text')) }}
              </small>
            </div>
          </div>
          <!-- --- --->

          <!-- Upload Adjust Volume Sound -->
          <div class="col-md-6 mb-5" style="height: 8vh">
            <label class="pl-3 form-label mb-0 pt-2">{{ $t('iframe.titles.adjust_volume_audio') }}</label>
            <div class="upload-div h-100">
              <div v-if="!value.adjust_volume_audio" class="d-flex justify-content-start align-items-center col-12"  @click="uploadAdjustVolumeAudio" >
                <div class="d-flex justify-content-center w-100">
                  <span class="clickable d-flex w-50 pl-4 justify-content-end">
                    <span class="d-flex align-items-center mr-3" >
                      <i class="i-add-bg add-icon-wh"></i>
                    </span>
                    <span class="d-flex flex-column align-items-center justify-content-center">
                      <span class="upload_input" style="color: #171725">{{$t('iframe.messages.drag_and_drop_file_or')}}</span>
                      <span style="color: #8f76db">{{$t('iframe.messages.browser_computer')}}</span>
                    </span>
                  </span>
                  <span class="verical-line"></span>
                  <span class="clickable d-flex w-50 pr-4 justify-content-start">
                    <span class="d-flex flex-column justify-content-center">
                      <span style="color: #8E99B4">{{$t('iframe.messages.maximum_size')}}: 1.5MB </span>
                      <span style="color: #8E99B4">{{$t('iframe.messages.supported_formats')}}: mp3</span>
                    </span>
                  </span>
                </div>
              </div>
              <br/>
              <div class="d-flex justify-content-center col-12" v-if="!value.adjust_volume_audio">
                <input ref="uploadSoundFileInput" id="upload-file-input-sound" type="file" @click="formErrors.clear('adjust_volume_audio')" @input="onAdjustVolumeAudioFileSelected" hidden accept=".mp3" >
              </div>
              <div class="d-flex justify-content-center align-items-center col-12" v-if="value.adjust_volume_audio && typeof value.adjust_volume_audio === 'string'">
                <div class="col-md-2 w-100 d-flex justify-content-center">
                  <div class="btn btn__round btn_add">
                    <va-button
                      :disabled="!value.adjust_volume_audio"
                      @click.prevent="playSound"
                    >
                      <i class="fa fa-play"></i>
                    </va-button>
                  </div>
                </div>
                <i class="fa fa-times ml-1 clickable" @click.prevent="removeAdjustVolumeAudio"></i>
              </div>
              <div class="d-flex justify-content-center align-items-center col-12" v-if="value.adjust_volume_audio && typeof value.adjust_volume_audio !== 'string'">
                <label>{{value.adjust_volume_audio.name}}</label>
                <i class="fa fa-times ml-1 clickable" @click.prevent="removeAdjustVolumeAudio"></i>
              </div>

            </div>
            <small v-if="formErrors.has('adjust_volume_audio')" class="help text-danger">
              {{$t(formErrors.get('adjust_volume_audio'))}}
            </small>
          </div>
          <!-- --- --->

          <div class="row col-12">
            <div class="col-md-6 col-6">
              <div>
                <label class="form-label">{{ $t('iframe.blocks_builder.tonal_block.default_output_source') }}</label>
                <multiselect
                  :options="outputSourcesOptions"
                  id="lang"
                  v-model="value.default_output_source"
                  label="label"
                  track-by="id"
                  select-label=""
                  selected-label=""
                  deselect-label=""
                  placeholder="---"
                  open-direction="bottom"
                  :allow-empty="true"
                  :multiple="false"
                  :searchable="true"
                >
                  <span slot="noResult">{{ $t('patients.noResults') }}</span>
                </multiselect>

              </div>
            </div>

            <!-- Override Redirect URL -->
            <div class="row col-md-6">
              <div class="col-12">
                <va-input
                  id="overrideRedirectUrl"
                  type="text"
                  v-model="value.override_redirect_url"
                  :label="$t('iframe.blocks_builder.tonal_block.override_redirect_url')"
                  :placeholder="$t('iframe.blocks_builder.tonal_block.override_redirect_url')"
                  @input="formErrors.clear('override_redirect_url')"
                />

                <small v-show="formErrors.has('override_redirect_url')" class="help text-danger">
                  {{ $t(formErrors.get('override_redirect_url')) }}
                </small>
              </div>
            </div>
            <!-- --- --->
          </div>

          <div class="row col-12">
            <div class="col-md-6 col-6">
              <span class="select is-fullwidth is-small">
                <va-checkbox
                  :label="$t('iframe.flows.auto_scroll_to_begin_of_page')"
                  id="checkbox-auto-scroll-to-begin-page"
                  :isCircle="true"
                  v-model="value.auto_scroll_to_begin_of_page"/>
              </span>
            </div>
          </div>

        </div>

      </form>
    </div>
  </div>

</template>

<script>
const errorsPrefix = 'iframe.flows.errors.'
import Errors from "@/utils/StandardErrors";
import Multiselect from 'vue-multiselect'
import * as countries from "i18n-iso-countries";
import Vue from "vue";

export default {
  components: {
    Multiselect,
  },
  props: {
    clientOptions: {
      type: Array,
      required: true,
      default: []
    },
    value: {
      type: Object,
      required: false
    },
    formErrors: {
      type: Object,
      required: false,
      default: () => {
        return new Errors()
      }
    },
    collapsable: {
      type: Boolean,
      default: false
    },
    collapsed: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      client: null,
      isCollapsed: this.collapsable && this.collapsed,
      langOptions: this.$store.state.languages,
      allowedAudioExtensions: ['mp3'],
      outputSourcesOptions: [
        {
          id:0,
          source: null,
          label: this.$t('iframe.flows.output_sources.default_both')
        },
        {
          id:1,
          source: 'headphones',
          label: this.$t('audiology_4_all.values.outputType.headphones')
        },
        {
          id:2,
          source: 'computer',
          label: this.$t('audiology_4_all.values.outputType.speakers')
        },
      ],
    }
  },
  mounted() {
    if(this.value && this.value.country) {
      this.value.country = this.countries.find(element => element.code.toUpperCase() === this.value.country.toUpperCase())
    }
    if(this.value && this.value.client_id) {
      const clientFound = this.clientOptions.find(elem => elem.id === parseInt(this.value.client_id))
      Vue.set(this.value, 'client', clientFound);
      this.client = clientFound
    }
    if(this.clientOptions.length === 1) {
      Vue.set(this.value, 'client', this.clientOptions[0]);
      //this.value.client = this.clientOptions[0]
      this.client = this.clientOptions[0]
    }
  },
  methods: {

    playSound: function() {
      new Audio(this.value.adjust_volume_audio).play()
    },
    onAdjustVolumeAudioFileSelected: function (event) {
      let files = event.target.files || event.dataTransfer.files
      if (!files.length) {
        return
      }
      if(files[0].size > 1.6 * 1024 * 1024) {
        this.formErrors.record({
          "adjust_volume_audio": ["file_size_too_big."]
        }, errorsPrefix)
        return
      }
      this.value.adjust_volume_audio = files[0]
    },
    uploadAdjustVolumeAudio: function () {
      this.formErrors.clear('adjust_volume_audio')
      this.$refs.uploadSoundFileInput.click()
    },
    removeAdjustVolumeAudio: function () {
      this.value.adjust_volume_audio = null
    }
  },
  computed: {
    envType: function () {
      return process.env.VUE_APP_ENVIRONMENT
    },
    countries: function () {
      for (let code of this.$store.state.countries.allowed) {
        if (code.toLowerCase() === "gb") {
          code = "en"
        }
        countries.registerLocale(require(`i18n-iso-countries/langs/${code.toLowerCase()}.json`))
      }
      let tempCountries = countries.getNames(Vue.i18n.locale().toLowerCase() === 'gb' ? 'en' : Vue.i18n.locale().toLowerCase())
      let countriesList = []
      for(const key in tempCountries) {
        countriesList.push({
          name: tempCountries[key],
          code: key
        })
      }
      return countriesList
      //return CountryList.filter(country => this.$store.state.countries.allowed.includes(country.code))
      //return CountryList
    },
  },
  watch: {
    client: function (val) {
      if (val) {
        Vue.set(this.value, 'client', val);
        this.$emit('redrawComponents')
      }
    }
  }
}
</script>

<style scoped>
.evollu-form .row {
  margin: 0 0 1.25rem !important;
}
.add-icon-wh{
  width: 2.5rem;
  height: 2.5rem;
}
.verical-line{
  width: 3px;
  height: 33px;
  background: #8E99B4;
  border-radius: 1.5px;
  align-self: center;
  margin: 0 10px;
  display: flex;
}
.custom-border-outline {
  border: 2px solid rgba(65, 65, 65, 0.1);
  border-radius: 25px;
}

.upload-div {
  display: flex;
  font-size: 12px;
  background: #EAEAEA;
  border: 2px dashed rgba(65, 65, 65, 0.1);
  border-radius: 25px;
}

</style>
